import { emptyCleanerActivitySummary } from '../../services/performanceService'
import { isGenuineComplaint, isComplaintBreach } from './cleanerActivityUtils'

/** @typedef {{ workingAreaIds: number[], roomIds: number[], cleanerIds: number[], sortKey: string }} ActivityFilter */

const SORT_KEYS = {
  NEEDS_ATTENTION: 'needs_attention',
  BEST_AVG_RESPONSE: 'best_avg_response',
  WORST_AVG_RESPONSE: 'worst_avg_response',
  MOST_SLA_BREACHES: 'most_sla_breaches',
  MOST_OPEN_COMPLAINTS: 'most_open_complaints',
  MOST_WORK_TIME: 'most_work_time',
  LEAST_WORK_TIME: 'least_work_time',
}

export const DEFAULT_SORT_KEY = SORT_KEYS.NEEDS_ATTENTION

export const SORT_OPTIONS = [
  SORT_KEYS.NEEDS_ATTENTION,
  SORT_KEYS.BEST_AVG_RESPONSE,
  SORT_KEYS.WORST_AVG_RESPONSE,
  SORT_KEYS.MOST_SLA_BREACHES,
  SORT_KEYS.MOST_OPEN_COMPLAINTS,
  SORT_KEYS.MOST_WORK_TIME,
  SORT_KEYS.LEAST_WORK_TIME,
]

export const emptyActivityFilter = () => ({
  workingAreaIds: [],
  roomIds: [],
  cleanerIds: [],
  sortKey: DEFAULT_SORT_KEY,
})

export function hasActiveLocationFilter(filter) {
  if (!filter) return false
  return (
    (filter.workingAreaIds && filter.workingAreaIds.length > 0)
    || (filter.roomIds && filter.roomIds.length > 0)
  )
}

export function hasActiveCleanerFilter(filter) {
  if (!filter) return false
  return filter.cleanerIds && filter.cleanerIds.length > 0
}

export function hasActiveActivityFilter(filter) {
  return hasActiveLocationFilter(filter) || hasActiveCleanerFilter(filter)
}

/**
 * How many of cleaner / working area / room / sort differ from their defaults (the Filters button badge).
 * Unlike `hasActiveActivityFilter` this includes sort, which affects the list order but not the summary.
 */
export function countActiveActivityFilters(filter) {
  if (!filter) return 0
  const hasIds = (ids) => Array.isArray(ids) && ids.length > 0
  return [
    hasIds(filter.cleanerIds),
    hasIds(filter.workingAreaIds),
    hasIds(filter.roomIds),
    !!filter.sortKey && filter.sortKey !== DEFAULT_SORT_KEY,
  ].filter(Boolean).length
}

/**
 * Site rooms narrowed by the selected room / working-area filters.
 * @param {{ rooms?: Array<{ id: number, building?: string, floor?: string, is_calculated?: number }> }} filterOptions
 * @param {ActivityFilter} selection
 * @param {Record<number, number[]>} workingAreaRoomMap workingAreaId -> roomIds
 * @returns {Set<number>|null} null when no location filter is active
 */
export function getEffectiveRoomIds(filterOptions, selection, workingAreaRoomMap = {}) {
  if (!hasActiveLocationFilter(selection)) return null

  let rooms = (filterOptions && filterOptions.rooms) || []
  rooms = rooms.filter((room) => room.is_calculated === 1 || room.is_calculated == null)

  if (selection.roomIds && selection.roomIds.length) {
    const roomIdSet = new Set(selection.roomIds.map(Number))
    rooms = rooms.filter((room) => roomIdSet.has(Number(room.id)))
  }
  if (selection.workingAreaIds && selection.workingAreaIds.length) {
    const waRoomIds = new Set()
    selection.workingAreaIds.forEach((waId) => {
      (workingAreaRoomMap[waId] || []).forEach((roomId) => waRoomIds.add(Number(roomId)))
    })
    rooms = rooms.filter((room) => waRoomIds.has(Number(room.id)))
  }

  return new Set(rooms.map((room) => Number(room.id)))
}

/**
 * @param {{ roomIds?: number[], workingAreaIds?: number[] }} cleaner
 * @param {Set<number>|null} effectiveRoomIds
 * @param {ActivityFilter} selection
 */
export function cleanerMatchesLocationFilter(cleaner, effectiveRoomIds, selection) {
  if (!effectiveRoomIds) return true

  const assignedRoomIds = new Set((cleaner.roomIds || []).map(Number))
  for (const roomId of effectiveRoomIds) {
    if (assignedRoomIds.has(roomId)) return true
  }

  if (selection.workingAreaIds && selection.workingAreaIds.length) {
    const cleanerWaIds = (cleaner.workingAreaIds || []).map(Number)
    return selection.workingAreaIds.some((waId) => cleanerWaIds.includes(Number(waId)))
  }

  return false
}

/**
 * @param {Array<Object>} cleaners
 * @param {number[]} cleanerIds
 */
export function filterCleanersByIds(cleaners, cleanerIds) {
  if (!cleanerIds || !cleanerIds.length) return cleaners
  const idSet = new Set(cleanerIds.map(Number))
  return cleaners.filter((cleaner) => idSet.has(Number(cleaner.employeeId)))
}

/**
 * Filter timeline SLA rows to the active room scope (when set).
 * @param {Array<Object>} cleaners
 * @param {Set<number>|null} effectiveRoomIds
 */
export function scopeCleanersToRooms(cleaners, effectiveRoomIds) {
  if (!effectiveRoomIds) return cleaners
  return cleaners.map((cleaner) => ({
    ...cleaner,
    complaints: (cleaner.complaints || []).filter(
      (complaint) => effectiveRoomIds.has(Number(complaint.roomId)),
    ),
  }))
}

/**
 * Derive SLA response metrics from a cleaner's (possibly scoped) complaints list.
 * @param {Array<Object>} complaints
 * @param {number|null} slaMinutes
 * @param {number} nowMs
 */
export function deriveComplaintMetrics(complaints, slaMinutes = null, nowMs = Date.now()) {
  const list = complaints || []
  const openComplaints = list.filter((complaint) => !complaint.closeTime).length
  const slaBreaches = list.filter((complaint) => isComplaintBreach(complaint, slaMinutes, nowMs)).length
  const responseTimesMs = list
    .filter(
      (complaint) =>
        complaint.closeTime
        && isGenuineComplaint(complaint)
        && Number.isFinite(Number(complaint.responseTime))
        && Number(complaint.responseTime) >= 0,
    )
    .map((complaint) => Number(complaint.responseTime))
  const avgResponseMinutes = responseTimesMs.length
    ? Math.round(responseTimesMs.reduce((sum, ms) => sum + ms, 0) / responseTimesMs.length / 60000)
    : null

  return {
    avgResponseMinutes,
    openComplaints,
    slaBreaches,
  }
}

/**
 * Merge scoped complaint metrics into each cleaner's metrics for display/sorting.
 * @param {Array<Object>} cleaners
 * @param {{ slaMinutes?: number|null, nowMs?: number }} options
 */
export function enrichCleanersMetrics(cleaners, { slaMinutes = null, nowMs = Date.now() } = {}) {
  return cleaners.map((cleaner) => ({
    ...cleaner,
    metrics: {
      ...(cleaner.metrics || {}),
      ...deriveComplaintMetrics(cleaner.complaints || [], slaMinutes, nowMs),
    },
  }))
}

function compareUsername(a, b) {
  return String(a.cleaner?.username || a.username || '').localeCompare(String(b.cleaner?.username || b.username || ''))
}

function nullsLastAsc(a, b) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return a - b
}

function nullsLastDesc(a, b) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return b - a
}

function needsAttentionScore(cleaner) {
  const metrics = cleaner.metrics || {}
  let score = 0
  score += (metrics.slaBreaches || 0) * 10000
  score += (metrics.openComplaints || 0) * 1000
  if (
    !metrics.isClockedIn
    && ((metrics.openComplaints || 0) > 0 || (metrics.slaBreaches || 0) > 0)
  ) {
    score += 500
  }
  if (metrics.avgResponseMinutes != null) score += metrics.avgResponseMinutes
  return score
}

/**
 * Sort cleaners by the selected ranking key (does not filter).
 * @param {Array<Object>} cleaners
 * @param {string} sortKey
 */
export function sortCleaners(cleaners, sortKey = DEFAULT_SORT_KEY) {
  const list = [...cleaners]
  const key = sortKey || DEFAULT_SORT_KEY

  list.sort((a, b) => {
    const ma = a.metrics || {}
    const mb = b.metrics || {}

    switch (key) {
      case SORT_KEYS.BEST_AVG_RESPONSE: {
        const cmp = nullsLastAsc(ma.avgResponseMinutes, mb.avgResponseMinutes)
        return cmp !== 0 ? cmp : compareUsername(a, b)
      }
      case SORT_KEYS.WORST_AVG_RESPONSE: {
        const cmp = nullsLastDesc(ma.avgResponseMinutes, mb.avgResponseMinutes)
        return cmp !== 0 ? cmp : compareUsername(a, b)
      }
      case SORT_KEYS.MOST_SLA_BREACHES: {
        const cmp = (mb.slaBreaches || 0) - (ma.slaBreaches || 0)
        return cmp !== 0 ? cmp : compareUsername(a, b)
      }
      case SORT_KEYS.MOST_OPEN_COMPLAINTS: {
        const cmp = (mb.openComplaints || 0) - (ma.openComplaints || 0)
        return cmp !== 0 ? cmp : compareUsername(a, b)
      }
      case SORT_KEYS.MOST_WORK_TIME: {
        const cmp = (mb.totalWorkMinutes || 0) - (ma.totalWorkMinutes || 0)
        return cmp !== 0 ? cmp : compareUsername(a, b)
      }
      case SORT_KEYS.LEAST_WORK_TIME: {
        const cmp = (ma.totalWorkMinutes || 0) - (mb.totalWorkMinutes || 0)
        return cmp !== 0 ? cmp : compareUsername(a, b)
      }
      case SORT_KEYS.NEEDS_ATTENTION:
      default: {
        const cmp = needsAttentionScore(b) - needsAttentionScore(a)
        return cmp !== 0 ? cmp : compareUsername(a, b)
      }
    }
  })

  return list
}

/** Recompute KPI summary for a filtered cleaner list. */
export function summarizeCleanerActivity(cleaners, { slaMinutes = null, nowMs = Date.now() } = {}) {
  if (!cleaners.length) return emptyCleanerActivitySummary()

  let activeCleaners = 0
  let totalWorkMinutes = 0
  let totalBreakMinutes = 0

  cleaners.forEach((cleaner) => {
    const metrics = cleaner.metrics || {}
    if (metrics.isClockedIn) activeCleaners += 1
    totalWorkMinutes += metrics.totalWorkMinutes || 0
    totalBreakMinutes += metrics.totalBreakMinutes || 0
  })

  const uniqueComplaints = new Map()
  cleaners.forEach((cleaner) => {
    (cleaner.complaints || []).forEach((complaint) => {
      if (!uniqueComplaints.has(complaint.slaId)) uniqueComplaints.set(complaint.slaId, complaint)
    })
  })
  const allComplaints = [...uniqueComplaints.values()]
  const openComplaints = allComplaints.filter((complaint) => !complaint.closeTime).length
  const closedComplaints = allComplaints.filter(
    (complaint) => complaint.closeTime && isGenuineComplaint(complaint),
  ).length
  const slaBreaches = allComplaints.filter(
    (complaint) => isComplaintBreach(complaint, slaMinutes, nowMs),
  ).length
  const responseTimesMs = allComplaints
    .filter(
      (complaint) =>
        complaint.closeTime
        && isGenuineComplaint(complaint)
        && Number.isFinite(Number(complaint.responseTime))
        && Number(complaint.responseTime) >= 0,
    )
    .map((complaint) => Number(complaint.responseTime))
  const avgResponseMinutes = responseTimesMs.length
    ? Math.round(responseTimesMs.reduce((sum, ms) => sum + ms, 0) / responseTimesMs.length / 60000)
    : null

  return {
    totalCleaners: cleaners.length,
    activeCleaners,
    closedComplaints,
    totalWorkMinutes,
    totalBreakMinutes,
    avgResponseMinutes,
    openComplaints,
    slaBreaches,
  }
}
